import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Plus, Trash2, Image as ImageIcon, RotateCcw, ExternalLink } from "lucide-react";
import { useAdminAuth, LoginScreen } from "./adminAuth";
import AdminHeader from "../components/AdminHeader";
import { useContent, DEFAULT_CONTENT } from "../context/ContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2MB after upload warning
const ICON_CHOICES = ["heart", "wine", "cake", "tent", "utensils", "music", "sparkles"];

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function AdminContent() {
  const { password, authed, checking, login, logout } = useAdminAuth();
  const { reload: reloadContent } = useContent();
  const [form, setForm] = useState(DEFAULT_CONTENT);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/content`);
      setForm({ ...DEFAULT_CONTENT, ...(data || {}) });
    } catch {
      toast.error("Inhalte konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  if (checking) return <div className="min-h-screen bg-sage-50" />;
  if (!authed) return <LoginScreen onSubmit={login} />;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, form, { params: { password } });
      await reloadContent();
      toast.success("Änderungen gespeichert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const resetField = (k) => set(k, DEFAULT_CONTENT[k]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sage-50">
        <AdminHeader onLogout={logout} />
        <div className="container mx-auto px-6 py-16 text-center text-sage-500">
          Inhalte werden geladen...
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-content" className="min-h-screen bg-sage-50 pb-32">
      <AdminHeader onLogout={logout} />

      <main className="container mx-auto px-6 md:px-12 py-10">
        <div className="flex items-start md:items-center justify-between flex-col md:flex-row gap-4 mb-8">
          <div>
            <h2 className="font-serif text-2xl md:text-3xl text-sage-800">
              Inhalte bearbeiten
            </h2>
            <p className="text-sm text-sage-600 mt-1">
              Alle Änderungen werden sofort live, sobald du speicherst.
            </p>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-sage-300 text-sage-700 hover:bg-white px-4 py-2 text-xs uppercase tracking-wide"
          >
            <ExternalLink size={14} /> Website öffnen
          </a>
        </div>

        <div className="space-y-8">
          {/* HERO */}
          <Section title="1. Start / Hero" testid="section-hero">
            <Grid>
              <Input label="Datum-Zeile (klein oben)" value={form.hero_eyebrow} onChange={(v) => set("hero_eyebrow", v)} onReset={() => resetField("hero_eyebrow")} testid="hero_eyebrow" />
              <Input label="Namen (großer Schriftzug)" value={form.hero_names} onChange={(v) => set("hero_names", v)} onReset={() => resetField("hero_names")} testid="hero_names" />
              <Input label="Zwischenzeile" value={form.hero_tagline} onChange={(v) => set("hero_tagline", v)} onReset={() => resetField("hero_tagline")} testid="hero_tagline" />
              <Input label="Untertitel" value={form.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} onReset={() => resetField("hero_subtitle")} testid="hero_subtitle" />
              <Input label="Button-Text" value={form.hero_cta} onChange={(v) => set("hero_cta", v)} onReset={() => resetField("hero_cta")} testid="hero_cta" />
            </Grid>
            <ImageField
              label="Hintergrundbild (Hero)"
              value={form.hero_image}
              onChange={(v) => set("hero_image", v)}
              onReset={() => resetField("hero_image")}
              testid="hero_image"
            />
          </Section>

          {/* STORY */}
          <Section title="2. Unsere Geschichte" testid="section-story">
            <Grid>
              <Input label="Kleiner Titel oben" value={form.story_eyebrow} onChange={(v) => set("story_eyebrow", v)} onReset={() => resetField("story_eyebrow")} testid="story_eyebrow" />
              <Input label="Überschrift-Anfang" value={form.story_title_prefix} onChange={(v) => set("story_title_prefix", v)} onReset={() => resetField("story_title_prefix")} testid="story_title_prefix" />
              <Input label="Überschrift-Schnörkel" value={form.story_title_script} onChange={(v) => set("story_title_script", v)} onReset={() => resetField("story_title_script")} testid="story_title_script" />
            </Grid>
            <Textarea label="Absatz 1" value={form.story_p1} onChange={(v) => set("story_p1", v)} onReset={() => resetField("story_p1")} testid="story_p1" rows={4} />
            <Textarea label="Absatz 2" value={form.story_p2} onChange={(v) => set("story_p2", v)} onReset={() => resetField("story_p2")} testid="story_p2" rows={4} />
            <Textarea label="Absatz 3" value={form.story_p3} onChange={(v) => set("story_p3", v)} onReset={() => resetField("story_p3")} testid="story_p3" rows={3} />
            <ImageField
              label="Bild (Liebesgeschichte)"
              value={form.story_image}
              onChange={(v) => set("story_image", v)}
              onReset={() => resetField("story_image")}
              testid="story_image"
            />
          </Section>

          {/* TIMELINE */}
          <Section title="3. Zeitplan" testid="section-timeline">
            <Grid>
              <Input label="Kleiner Titel" value={form.timeline_eyebrow} onChange={(v) => set("timeline_eyebrow", v)} onReset={() => resetField("timeline_eyebrow")} testid="timeline_eyebrow" />
              <Input label="Überschrift-Anfang" value={form.timeline_title_prefix} onChange={(v) => set("timeline_title_prefix", v)} onReset={() => resetField("timeline_title_prefix")} testid="timeline_title_prefix" />
              <Input label="Überschrift-Schnörkel" value={form.timeline_title_script} onChange={(v) => set("timeline_title_script", v)} onReset={() => resetField("timeline_title_script")} testid="timeline_title_script" />
            </Grid>
            <Textarea label="Untertitel" value={form.timeline_subtitle} onChange={(v) => set("timeline_subtitle", v)} onReset={() => resetField("timeline_subtitle")} testid="timeline_subtitle" rows={2} />

            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-serif text-lg text-sage-800">Ereignisse</h4>
                <button
                  data-testid="timeline-add-event"
                  type="button"
                  onClick={() => set("timeline_events", [...(form.timeline_events || []), { time: "12:00", icon: "sparkles", title: "Neu", description: "" }])}
                  className="inline-flex items-center gap-1 text-xs text-sage-600 hover:text-sage-800 border border-sage-300 rounded-full px-3 py-1.5"
                >
                  <Plus size={14} /> Ereignis hinzufügen
                </button>
              </div>
              <div className="space-y-4">
                {(form.timeline_events || []).map((ev, i) => (
                  <div key={i} data-testid={`timeline-event-editor-${i}`} className="bg-sage-50 border border-sage-200 rounded-xl p-4">
                    <div className="grid grid-cols-1 md:grid-cols-[110px_160px_1fr_auto] gap-3">
                      <input
                        value={ev.time}
                        onChange={(e) => {
                          const ne = [...form.timeline_events];
                          ne[i] = { ...ne[i], time: e.target.value };
                          set("timeline_events", ne);
                        }}
                        className={inputCls}
                        placeholder="14:00"
                      />
                      <select
                        value={ev.icon || "heart"}
                        onChange={(e) => {
                          const ne = [...form.timeline_events];
                          ne[i] = { ...ne[i], icon: e.target.value };
                          set("timeline_events", ne);
                        }}
                        className={inputCls}
                      >
                        {ICON_CHOICES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                      <input
                        value={ev.title}
                        onChange={(e) => {
                          const ne = [...form.timeline_events];
                          ne[i] = { ...ne[i], title: e.target.value };
                          set("timeline_events", ne);
                        }}
                        className={inputCls}
                        placeholder="Titel"
                      />
                      <button
                        type="button"
                        onClick={() => set("timeline_events", form.timeline_events.filter((_, idx) => idx !== i))}
                        className="inline-flex items-center justify-center rounded-lg bg-white border border-red-200 text-red-500 hover:bg-red-50 p-2"
                        aria-label="Löschen"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <textarea
                      value={ev.description}
                      onChange={(e) => {
                        const ne = [...form.timeline_events];
                        ne[i] = { ...ne[i], description: e.target.value };
                        set("timeline_events", ne);
                      }}
                      rows={2}
                      className={`${inputCls} mt-3 resize-none`}
                      placeholder="Beschreibung"
                    />
                  </div>
                ))}
              </div>
            </div>
          </Section>

          {/* LOCATION */}
          <Section title="4. Location" testid="section-location">
            <Grid>
              <Input label="Kleiner Titel" value={form.location_eyebrow} onChange={(v) => set("location_eyebrow", v)} onReset={() => resetField("location_eyebrow")} testid="location_eyebrow" />
              <Input label="Überschrift-Anfang" value={form.location_title_prefix} onChange={(v) => set("location_title_prefix", v)} onReset={() => resetField("location_title_prefix")} testid="location_title_prefix" />
              <Input label="Überschrift-Schnörkel" value={form.location_title_script} onChange={(v) => set("location_title_script", v)} onReset={() => resetField("location_title_script")} testid="location_title_script" />
            </Grid>
            <Textarea label="Beschreibung" value={form.location_description} onChange={(v) => set("location_description", v)} onReset={() => resetField("location_description")} testid="location_description" rows={3} />
            <Grid>
              <Input label="Datum & Ankunftszeit (groß)" value={form.location_date_main} onChange={(v) => set("location_date_main", v)} onReset={() => resetField("location_date_main")} testid="location_date_main" />
              <Input label="Untertitel (klein)" value={form.location_date_sub} onChange={(v) => set("location_date_sub", v)} onReset={() => resetField("location_date_sub")} testid="location_date_sub" />
            </Grid>
            <Input label="Google-Maps URL" value={form.location_maps_url} onChange={(v) => set("location_maps_url", v)} onReset={() => resetField("location_maps_url")} testid="location_maps_url" />
            <Input label="Wegbeschreibung Überschrift" value={form.location_directions_title} onChange={(v) => set("location_directions_title", v)} onReset={() => resetField("location_directions_title")} testid="location_directions_title" />

            <div className="mt-4">
              <div className="flex items-center justify-between mb-3">
                <span className="block text-xs uppercase tracking-[0.2em] text-sage-600">Wegbeschreibung-Schritte</span>
                <button
                  type="button"
                  onClick={() => set("location_directions", [...(form.location_directions || []), ""])}
                  className="inline-flex items-center gap-1 text-xs text-sage-600 hover:text-sage-800 border border-sage-300 rounded-full px-3 py-1"
                >
                  <Plus size={12} /> Schritt
                </button>
              </div>
              <div className="space-y-2">
                {(form.location_directions || []).map((step, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="font-serif text-sage-500 pt-2">{i + 1}.</span>
                    <textarea
                      rows={2}
                      value={step}
                      onChange={(e) => {
                        const ne = [...form.location_directions];
                        ne[i] = e.target.value;
                        set("location_directions", ne);
                      }}
                      className={`${inputCls} flex-1 resize-none`}
                    />
                    <button
                      type="button"
                      onClick={() => set("location_directions", form.location_directions.filter((_, idx) => idx !== i))}
                      className="rounded-lg bg-white border border-red-200 text-red-500 hover:bg-red-50 p-2"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <ImageField
              label="Location-Bild"
              value={form.location_image}
              onChange={(v) => set("location_image", v)}
              onReset={() => resetField("location_image")}
              testid="location_image"
            />
          </Section>

          {/* HOTEL */}
          <Section title="5. Übernachtung / Hotel" testid="section-hotel">
            <Grid>
              <Input label="Kleiner Titel" value={form.hotel_eyebrow} onChange={(v) => set("hotel_eyebrow", v)} onReset={() => resetField("hotel_eyebrow")} testid="hotel_eyebrow" />
              <Input label="Überschrift-Anfang" value={form.hotel_title_prefix} onChange={(v) => set("hotel_title_prefix", v)} onReset={() => resetField("hotel_title_prefix")} testid="hotel_title_prefix" />
              <Input label="Überschrift-Schnörkel" value={form.hotel_title_script} onChange={(v) => set("hotel_title_script", v)} onReset={() => resetField("hotel_title_script")} testid="hotel_title_script" />
            </Grid>
            <Textarea label="Haupttext" value={form.hotel_description} onChange={(v) => set("hotel_description", v)} onReset={() => resetField("hotel_description")} testid="hotel_description" rows={3} />
            <Textarea label="Details (kleinerer Text)" value={form.hotel_details} onChange={(v) => set("hotel_details", v)} onReset={() => resetField("hotel_details")} testid="hotel_details" rows={2} />
            <Textarea label="Abschließender Satz" value={form.hotel_cta} onChange={(v) => set("hotel_cta", v)} onReset={() => resetField("hotel_cta")} testid="hotel_cta" rows={2} />

            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-serif text-lg text-sage-800">Zimmer-Karten (Preise)</h4>
                <button
                  data-testid="rooms-add"
                  type="button"
                  onClick={() =>
                    set("hotel_rooms", [
                      ...(form.hotel_rooms || []),
                      { name: "Neues Zimmer", price: "ab 0 €", detail: "" },
                    ])
                  }
                  className="inline-flex items-center gap-1 text-xs text-sage-600 hover:text-sage-800 border border-sage-300 rounded-full px-3 py-1.5"
                >
                  <Plus size={14} /> Zimmer hinzufügen
                </button>
              </div>
              <div className="space-y-4">
                {(form.hotel_rooms || []).map((room, i) => (
                  <div
                    key={i}
                    data-testid={`room-editor-${i}`}
                    className="bg-sage-50 border border-sage-200 rounded-xl p-4"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_auto] gap-3">
                      <input
                        value={room.name}
                        onChange={(e) => {
                          const ne = [...form.hotel_rooms];
                          ne[i] = { ...ne[i], name: e.target.value };
                          set("hotel_rooms", ne);
                        }}
                        className={inputCls}
                        placeholder="Zimmer-Name (z.B. Doppelzimmer)"
                      />
                      <input
                        value={room.price}
                        onChange={(e) => {
                          const ne = [...form.hotel_rooms];
                          ne[i] = { ...ne[i], price: e.target.value };
                          set("hotel_rooms", ne);
                        }}
                        className={inputCls}
                        placeholder="ab 138 €"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          set(
                            "hotel_rooms",
                            form.hotel_rooms.filter((_, idx) => idx !== i)
                          )
                        }
                        className="inline-flex items-center justify-center rounded-lg bg-white border border-red-200 text-red-500 hover:bg-red-50 p-2"
                        aria-label="Löschen"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <input
                      value={room.detail}
                      onChange={(e) => {
                        const ne = [...form.hotel_rooms];
                        ne[i] = { ...ne[i], detail: e.target.value };
                        set("hotel_rooms", ne);
                      }}
                      className={`${inputCls} mt-3`}
                      placeholder="Details (z.B. 22 Zimmer · 13–19 m²)"
                    />
                  </div>
                ))}
              </div>
            </div>

            <Grid>
              <Input label="Fußnote unter den Karten" value={form.hotel_rooms_footnote} onChange={(v) => set("hotel_rooms_footnote", v)} onReset={() => resetField("hotel_rooms_footnote")} testid="hotel_rooms_footnote" />
              <Input label="Button-Text (zur Hotel-Website)" value={form.hotel_link_label} onChange={(v) => set("hotel_link_label", v)} onReset={() => resetField("hotel_link_label")} testid="hotel_link_label" />
            </Grid>
            <Input label="Button-URL" value={form.hotel_link_url} onChange={(v) => set("hotel_link_url", v)} onReset={() => resetField("hotel_link_url")} testid="hotel_link_url" />
          </Section>

          {/* RSVP */}
          <Section title="6. RSVP (Zusage-Bereich)" testid="section-rsvp">
            <Grid>
              <Input label="Kleiner Titel" value={form.rsvp_eyebrow} onChange={(v) => set("rsvp_eyebrow", v)} onReset={() => resetField("rsvp_eyebrow")} testid="rsvp_eyebrow" />
              <Input label="Überschrift-Anfang" value={form.rsvp_title_prefix} onChange={(v) => set("rsvp_title_prefix", v)} onReset={() => resetField("rsvp_title_prefix")} testid="rsvp_title_prefix" />
              <Input label="Überschrift-Schnörkel" value={form.rsvp_title_script} onChange={(v) => set("rsvp_title_script", v)} onReset={() => resetField("rsvp_title_script")} testid="rsvp_title_script" />
            </Grid>
            <Input label="Rückmeldung bis" value={form.rsvp_deadline} onChange={(v) => set("rsvp_deadline", v)} onReset={() => resetField("rsvp_deadline")} testid="rsvp_deadline" />
            <Textarea
              label="Einleitungstext (benutze {deadline} als Platzhalter)"
              value={form.rsvp_description}
              onChange={(v) => set("rsvp_description", v)}
              onReset={() => resetField("rsvp_description")}
              testid="rsvp_description"
              rows={3}
            />
          </Section>
        </div>
      </main>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-sage-200 shadow-lg z-20">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-4">
          <span className="text-sm text-sage-600 hidden sm:inline">
            Änderungen erst nach „Speichern" live.
          </span>
          <div className="flex gap-3 ml-auto">
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-full border border-sage-300 text-sage-700 hover:bg-sage-50 px-4 py-2 text-sm"
            >
              Verwerfen
            </button>
            <button
              data-testid="content-save-btn"
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-sage-500 hover:bg-sage-600 disabled:bg-sage-300 text-white px-6 py-2.5 text-sm uppercase tracking-wide"
            >
              <Save size={16} />
              {saving ? "Speichert..." : "Speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-sage-300 bg-white px-3 py-2 text-sm text-sage-800 placeholder:text-sage-400 focus:outline-none focus:ring-2 focus:ring-sage-400";

const Section = ({ title, testid, children }) => (
  <section
    data-testid={testid}
    className="bg-white rounded-2xl border border-sage-200 shadow-sm p-6 md:p-8"
  >
    <h3 className="font-serif text-xl md:text-2xl text-sage-800 mb-6 border-b border-sage-100 pb-3">
      {title}
    </h3>
    <div className="space-y-4">{children}</div>
  </section>
);

const Grid = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;

const LabelRow = ({ label, onReset }) => (
  <div className="flex items-center justify-between mb-1.5">
    <span className="text-xs uppercase tracking-[0.2em] text-sage-600">{label}</span>
    {onReset && (
      <button
        type="button"
        onClick={onReset}
        className="text-[10px] uppercase tracking-wide text-sage-400 hover:text-sage-700 inline-flex items-center gap-1"
      >
        <RotateCcw size={10} /> Standard
      </button>
    )}
  </div>
);

const Input = ({ label, value, onChange, onReset, testid }) => (
  <label className="block">
    <LabelRow label={label} onReset={onReset} />
    <input
      data-testid={`field-${testid}`}
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className={inputCls}
    />
  </label>
);

const Textarea = ({ label, value, onChange, onReset, rows = 3, testid }) => (
  <label className="block">
    <LabelRow label={label} onReset={onReset} />
    <textarea
      data-testid={`field-${testid}`}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className={`${inputCls} resize-y`}
    />
  </label>
);

const ImageField = ({ label, value, onChange, onReset, testid }) => {
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Bild zu groß (max 2 MB). Bitte verkleinern.");
      return;
    }
    const dataUrl = await readFileAsDataURL(file);
    onChange(dataUrl);
  };

  return (
    <div>
      <LabelRow label={label} onReset={onReset} />
      <div className="flex flex-col md:flex-row gap-4">
        <div className="md:w-48 h-32 rounded-xl overflow-hidden bg-sage-100 border border-sage-200 flex items-center justify-center shrink-0">
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="text-sage-400" size={28} />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            data-testid={`field-${testid}-url`}
            type="text"
            value={value && value.startsWith("data:") ? "" : value || ""}
            placeholder="Bild-URL (oder Datei unten hochladen)"
            onChange={(e) => onChange(e.target.value)}
            className={inputCls}
          />
          <label className="flex items-center gap-2 text-sm text-sage-600 cursor-pointer hover:text-sage-800">
            <ImageIcon size={14} />
            <span>Datei hochladen (max. 2 MB)</span>
            <input
              data-testid={`field-${testid}-file`}
              type="file"
              accept="image/*"
              onChange={pick}
              className="hidden"
            />
          </label>
          {value && value.startsWith("data:") && (
            <p className="text-[11px] text-sage-500">
              ✓ Hochgeladenes Bild (wird in der Datenbank gespeichert)
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
